import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaCog, FaGlobe, FaEnvelope, FaDatabase, FaKey, FaWhatsapp, FaMicrosoft, FaMapMarkedAlt, FaSave, FaImage, FaPalette } from 'react-icons/fa';
import { getSettings, updateSettings, uploadFile } from '../../models/settingsModel';
import { API_BASE_URL, CUSTOMER_PORTAL_CONFIG, getCustomerPortalApi, switchCustomerPortalApiMode } from '../../config'; // Import API configuration
 

function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // API Configuration state
  const [apiMode, setApiMode] = useState(CUSTOMER_PORTAL_CONFIG.mode);
  const [currentApiConfig, setCurrentApiConfig] = useState(getCustomerPortalApi());
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', label: 'General', icon: FaCog },
    { id: 'theme', label: 'Theme', icon: FaPalette },
    { id: 'integrations', label: 'Integrations', icon: FaGlobe },
    { id: 'email', label: 'Email Services', icon: FaEnvelope },
    { id: 'database', label: 'Database', icon: FaDatabase },
  ];

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      if (response.status === 1) {
        setSettings(response.data);
      } else {
        toast.error(response.message || 'Failed to load settings.');
      }
    } catch (error) {
      toast.error('An error occurred while fetching settings.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings(prev => ({ ...prev, site_logo_preview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApiModeChange = (newMode) => {
    setApiMode(newMode);
    
    // Use the utility function to switch API mode
    if (switchCustomerPortalApiMode(newMode)) {
      // Update current API config display
      setCurrentApiConfig(getCustomerPortalApi());
      
      // Show success message
      toast.success(`Customer Portal API switched to ${newMode} mode. Please refresh the page for full effect.`);
    } else {
      toast.error('Invalid API mode selected.');
    }
  };

  // Load API mode from localStorage on component mount
  useEffect(() => {
    // Set initial state from current configuration
    setApiMode(CUSTOMER_PORTAL_CONFIG.mode);
    setCurrentApiConfig(getCustomerPortalApi());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    let settingsToSave = { ...settings };

    // If a new file was selected, upload it first
    if (selectedFile) {
      try {
        const uploadResponse = await uploadFile(selectedFile);
        if (uploadResponse.status === 1) {
          // If upload is successful, add the new filename to the settings to be saved
          settingsToSave.site_logo = uploadResponse.data.filename;
        } else {
          toast.error(uploadResponse.message || 'Logo upload failed.');
          setSaving(false);
          return;
        }
      } catch (error) {
        toast.error('An error occurred during logo upload.');
        console.error(error);
        setSaving(false);
        return;
      }
    }

    // Now, save all settings (including the new filename if there was one)
    try {
      const response = await updateSettings(settingsToSave);
      if (response.status === 1) {
        toast.success(response.message);
        // Refetch settings to get the latest state, including the new logo path
        fetchSettings();
      } else {
        toast.error(response.message || 'Failed to save settings.');
      }
    } catch (error) {
      toast.error('An error occurred while saving settings.');
      console.error(error);
    } finally {
      setSaving(false);
      setSelectedFile(null); // Reset selected file after submission attempt
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold custom-text-neutral-800">Application Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your application configuration and integrations</p>
        </div>
        <button 
          onClick={handleSubmit} 
          disabled={saving}
          className="flex items-center justify-center gap-2 custom-btn-secondary text-white font-semibold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <FaSave />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-xl shadow-sm p-1 flex overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-50 text-blue-600 shadow-sm'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <tab.icon className={activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'} />
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 animate-fadeIn">
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><FaCog /></div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Site Details</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="site_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">Site Name</label>
                    <input type="text" id="site_name" name="site_name" value={settings.site_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="site_email" className="block text-sm font-medium custom-text-neutral-700 mb-1">Site Email</label>
                    <input type="email" id="site_email" name="site_email" value={settings.site_email || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="support_email" className="block text-sm font-medium custom-text-neutral-700 mb-1">Support Email</label>
                    <input type="email" id="support_email" name="support_email" value={settings.support_email || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="form_email" className="block text-sm font-medium custom-text-neutral-700 mb-1">Form Submit Destination</label>
                    <input type="email" id="form_email" name="form_email" value={settings.form_email || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="test_email" className="block text-sm font-medium custom-text-neutral-700 mb-1">Test Environment Email</label>
                    <input type="email" id="test_email" name="test_email" value={settings.test_email || ''} onChange={handleInputChange} className="form-control" placeholder="test@example.com" />
                    <p className="text-xs custom-text-neutral-500 mt-1">All emails on quality.silulumanzi.com will be sent to this address</p>
                  </div>
                  <div>
                    <label htmlFor="site_contact_no" className="block text-sm font-medium custom-text-neutral-700 mb-1">Contact No</label>
                    <input type="text" id="site_contact_no" name="site_contact_no" value={settings.site_contact_no || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="site_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">Address</label>
                    <textarea id="site_address" name="site_address" rows="3" value={settings.site_address || ''} onChange={handleInputChange} className="form-control"></textarea>
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="site_footer" className="block text-sm font-medium custom-text-neutral-700 mb-1">Site Footer</label>
                    <input type="text" id="site_footer" name="site_footer" value={settings.site_footer || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="updates_url" className="block text-sm font-medium custom-text-neutral-700 mb-1">Updates URL</label>
                    <input type="url" id="updates_url" name="updates_url" value={settings.updates_url || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>

            <div className="xl:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-purple-50 rounded-lg text-purple-600"><FaImage /></div>
                  <h2 className="text-xl font-semibold text-neutral-800">Site Logo</h2>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-full h-48 mb-6 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center overflow-hidden bg-gray-50 relative group">
                    <img 
                      src={settings.site_logo_preview || (settings.site_logo ? `${API_BASE_URL}/images/${settings.site_logo}` : '')} 
                      alt="Logo Preview" 
                      className={`object-contain h-full w-full p-4 ${settings.site_logo_preview || settings.site_logo ? '' : 'hidden'}`} 
                    />
                    {(!settings.site_logo_preview && !settings.site_logo) && (
                      <div className="text-center p-4">
                        <FaImage className="mx-auto text-gray-300 text-4xl mb-2" />
                        <span className="text-gray-400 text-sm">No Logo Uploaded</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-transparent group-hover:bg-black group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                      <label htmlFor="site_logo_upload" className="opacity-0 group-hover:opacity-100 cursor-pointer bg-white text-gray-700 font-medium py-2 px-4 rounded-lg shadow-sm transform translate-y-2 group-hover:translate-y-0 transition-all">
                        Change Logo
                      </label>
                    </div>
                  </div>
                  <input type="file" id="site_logo_upload" name="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  <p className="text-xs text-gray-500 text-center">Recommended size: 200x100px. PNG or JPG format.</p>
                </div>
              </div>

              {/* Asset Declaration HOD Approval Toggle */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600"><FaKey /></div>
                  <h2 className="text-xl font-semibold text-neutral-800">Asset Management</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label htmlFor="asset_declaration_hod_approval" className="block text-sm font-medium custom-text-neutral-700">HOD Approval for Asset Declarations</label>
                      <p className="text-xs text-gray-500 mt-1">When enabled, asset declarations require HOD approval before being finalized</p>
                    </div>
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        id="asset_declaration_hod_approval" 
                        name="asset_declaration_hod_approval" 
                        checked={settings.asset_declaration_hod_approval === '1' || settings.asset_declaration_hod_approval === 1}
                        onChange={(e) => {
                          setSettings(prev => ({ 
                            ...prev, 
                            asset_declaration_hod_approval: e.target.checked ? '1' : '0' 
                          }));
                        }}
                        className="sr-only peer"
                      />
                      <label 
                        htmlFor="asset_declaration_hod_approval"
                        className="relative flex h-6 w-11 cursor-pointer items-center rounded-full bg-gray-300 px-0.5 outline-none transition-colors peer-checked:bg-indigo-500 peer-focus:ring-2 peer-focus:ring-indigo-300"
                      >
                        <span className="h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" style={{ transform: (settings.asset_declaration_hod_approval === '1' || settings.asset_declaration_hod_approval === 1) ? 'translateX(20px)' : 'translateX(0)' }}></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Management Settings */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><FaCog /></div>
                  <h2 className="text-xl font-semibold text-neutral-800">Task Management</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="task_approval_hours_limit" className="block text-sm font-medium custom-text-neutral-700 mb-1">
                      Task Approval Hours Limit
                    </label>
                    <input 
                      type="number" 
                      id="task_approval_hours_limit" 
                      name="task_approval_hours_limit" 
                      value={settings.task_approval_hours_limit || '8'} 
                      onChange={handleInputChange}
                      min="0"
                      step="0.5"
                      className="form-control" 
                      placeholder="8"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tasks with estimated hours above this limit will require approval before work can begin. Set to 0 to disable approval requirement.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Theme Tab */}
        {activeTab === 'theme' && (
          <div className="animate-fadeIn">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Sidebar Color Theme */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-800">Sidebar Theme</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Choose a color theme for the sidebar navigation.</p>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { id: 'default', name: 'Default Blue', gradient: 'linear-gradient(to bottom, #0099e0, #007acc)' },
                      { id: 'ocean', name: 'Ocean', gradient: 'linear-gradient(to bottom, #0077b6, #023e8a)' },
                      { id: 'forest', name: 'Forest', gradient: 'linear-gradient(to bottom, #2d6a4f, #1b4332)' },
                      { id: 'sunset', name: 'Sunset', gradient: 'linear-gradient(to bottom, #e85d04, #9d0208)' },
                      { id: 'purple', name: 'Purple', gradient: 'linear-gradient(to bottom, #7b2cbf, #5a189a)' },
                      { id: 'slate', name: 'Slate', gradient: 'linear-gradient(to bottom, #475569, #1e293b)' },
                      { id: 'teal', name: 'Teal', gradient: 'linear-gradient(to bottom, #0d9488, #115e59)' },
                      { id: 'rose', name: 'Rose', gradient: 'linear-gradient(to bottom, #e11d48, #9f1239)' },
                    ].map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => {
                          setSettings(prev => ({ ...prev, sidebar_theme: color.id }));
                          // Also save to localStorage for immediate effect
                          localStorage.setItem('sidebar_theme', color.id);
                          // Dispatch custom event to notify Sidebar component
                          window.dispatchEvent(new CustomEvent('sidebarThemeChange', { detail: color.id }));
                        }}
                        className={`relative h-16 rounded-lg transition-all hover:scale-105 ${
                          (settings.sidebar_theme || 'default') === color.id 
                            ? 'ring-2 ring-offset-2 ring-blue-500 scale-105' 
                            : 'hover:ring-2 hover:ring-offset-1 hover:ring-gray-300'
                        }`}
                        style={{ background: color.gradient }}
                        title={color.name}
                      >
                        {(settings.sidebar_theme || 'default') === color.id && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-6 h-6 text-white drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {[
                      { id: 'default', name: 'Default Blue' },
                      { id: 'ocean', name: 'Ocean' },
                      { id: 'forest', name: 'Forest' },
                      { id: 'sunset', name: 'Sunset' },
                      { id: 'purple', name: 'Purple' },
                      { id: 'slate', name: 'Slate' },
                      { id: 'teal', name: 'Teal' },
                      { id: 'rose', name: 'Rose' },
                    ].find(c => c.id === (settings.sidebar_theme || 'default'))?.name || 'Default Blue'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fadeIn">
            {/* Azure AD */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><FaMicrosoft /></div>
                <h2 className="text-xl font-semibold custom-text-neutral-800">Azure AD Configuration</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="azure_client_id" className="block text-sm font-medium custom-text-neutral-700 mb-1">Client ID</label>
                  <input type="text" id="azure_client_id" name="azure_client_id" value={settings.azure_client_id || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="azure_tenant_id" className="block text-sm font-medium custom-text-neutral-700 mb-1">Tenant ID</label>
                  <input type="text" id="azure_tenant_id" name="azure_tenant_id" value={settings.azure_tenant_id || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="azure_client_secret" className="block text-sm font-medium custom-text-neutral-700 mb-1">Client Secret</label>
                  <input type="password" id="azure_client_secret" name="azure_client_secret" value={settings.azure_client_secret || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="azure_redirect_url" className="block text-sm font-medium custom-text-neutral-700 mb-1">Redirect URL</label>
                  <input type="text" id="azure_redirect_url" name="azure_redirect_url" value={settings.azure_redirect_url || ''} onChange={handleInputChange} className="form-control" />
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-green-50 rounded-lg text-green-600"><FaWhatsapp /></div>
                <h2 className="text-xl font-semibold custom-text-neutral-800">WhatsApp Configuration</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="whatsapp_number_id" className="block text-sm font-medium custom-text-neutral-700 mb-1">WhatsApp Number ID</label>
                  <input type="text" id="whatsapp_number_id" name="whatsapp_number_id" value={settings.whatsapp_number_id || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="whatsapp_fb_token" className="block text-sm font-medium custom-text-neutral-700 mb-1">Facebook Token</label>
                  <textarea id="whatsapp_fb_token" name="whatsapp_fb_token" rows="4" value={settings.whatsapp_fb_token || ''} onChange={handleInputChange} className="form-control font-mono text-xs"></textarea>
                </div>
              </div>
            </div>

            {/* Ozow */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-orange-50 rounded-lg text-orange-600"><FaKey /></div>
                <h2 className="text-xl font-semibold custom-text-neutral-800">Ozow Configuration</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="site_ozow_mode" className="block text-sm font-medium custom-text-neutral-700 mb-1">Ozow Mode</label>
                  <select id="site_ozow_mode" name="site_ozow_mode" value={settings.site_ozow_mode || '0'} onChange={handleInputChange} className="form-control">
                    <option value="1">Live</option>
                    <option value="0">Test</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ozow_private_key" className="block text-sm font-medium custom-text-neutral-700 mb-1">Ozow Private Key</label>
                  <input type="password" id="ozow_private_key" name="ozow_private_key" value={settings.ozow_private_key || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="ozow_api_key" className="block text-sm font-medium custom-text-neutral-700 mb-1">Ozow API Key</label>
                  <input type="text" id="ozow_api_key" name="ozow_api_key" value={settings.ozow_api_key || ''} onChange={handleInputChange} className="form-control" />
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-red-50 rounded-lg text-red-600"><FaMapMarkedAlt /></div>
                <h2 className="text-xl font-semibold custom-text-neutral-800">Google Maps API</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="google_maps_api_key" className="block text-sm font-medium custom-text-neutral-700 mb-1">API Key</label>
                  <input type="text" id="google_maps_api_key" name="google_maps_api_key" value={settings.google_maps_api_key || ''} onChange={handleInputChange} className="form-control" placeholder="Enter your Google Maps API key" />
                  <p className="text-xs custom-text-neutral-500 mt-1">
                    Required for displaying maps in the MMS module. Get your API key from the{' '}
                    <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fadeIn">
            {/* Helpdesk Email */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600"><FaEnvelope /></div>
                <div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Helpdesk Email</h2>
                  <p className="text-xs text-gray-500">Used for ticket notifications and support</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_helpdesk_host" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Host</label>
                    <input type="text" id="email_helpdesk_host" name="email_helpdesk_host" value={settings.email_helpdesk_host || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_helpdesk_port" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Port</label>
                    <input type="text" id="email_helpdesk_port" name="email_helpdesk_port" value={settings.email_helpdesk_port || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email_helpdesk_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                  <input type="text" id="email_helpdesk_username" name="email_helpdesk_username" value={settings.email_helpdesk_username || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="email_helpdesk_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                  <input type="password" id="email_helpdesk_password" name="email_helpdesk_password" value={settings.email_helpdesk_password || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_helpdesk_from_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Address</label>
                    <input type="email" id="email_helpdesk_from_address" name="email_helpdesk_from_address" value={settings.email_helpdesk_from_address || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_helpdesk_from_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Name</label>
                    <input type="text" id="email_helpdesk_from_name" name="email_helpdesk_from_name" value={settings.email_helpdesk_from_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>

            {/* Portal Email */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-teal-50 rounded-lg text-teal-600"><FaEnvelope /></div>
                <div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Portal Email</h2>
                  <p className="text-xs text-gray-500">Used for general system notifications</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_portal_host" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Host</label>
                    <input type="text" id="email_portal_host" name="email_portal_host" value={settings.email_portal_host || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_portal_port" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Port</label>
                    <input type="text" id="email_portal_port" name="email_portal_port" value={settings.email_portal_port || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email_portal_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                  <input type="text" id="email_portal_username" name="email_portal_username" value={settings.email_portal_username || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="email_portal_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                  <input type="password" id="email_portal_password" name="email_portal_password" value={settings.email_portal_password || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_portal_from_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Address</label>
                    <input type="email" id="email_portal_from_address" name="email_portal_from_address" value={settings.email_portal_from_address || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_portal_from_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Name</label>
                    <input type="text" id="email_portal_from_name" name="email_portal_from_name" value={settings.email_portal_from_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>

            {/* Procurement Email */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-orange-50 rounded-lg text-orange-600"><FaEnvelope /></div>
                <div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Procurement Email</h2>
                  <p className="text-xs text-gray-500">Used for RFQ and procurement notifications</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_procurement_host" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Host</label>
                    <input type="text" id="email_procurement_host" name="email_procurement_host" value={settings.email_procurement_host || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_procurement_port" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Port</label>
                    <input type="text" id="email_procurement_port" name="email_procurement_port" value={settings.email_procurement_port || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email_procurement_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                  <input type="text" id="email_procurement_username" name="email_procurement_username" value={settings.email_procurement_username || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="email_procurement_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                  <input type="password" id="email_procurement_password" name="email_procurement_password" value={settings.email_procurement_password || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_procurement_from_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Address</label>
                    <input type="email" id="email_procurement_from_address" name="email_procurement_from_address" value={settings.email_procurement_from_address || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_procurement_from_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Name</label>
                    <input type="text" id="email_procurement_from_name" name="email_procurement_from_name" value={settings.email_procurement_from_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Email */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-purple-50 rounded-lg text-purple-600"><FaEnvelope /></div>
                <div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Bulk Email</h2>
                  <p className="text-xs text-gray-500">Used for mass communications and statements</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_bulk_host" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Host</label>
                    <input type="text" id="email_bulk_host" name="email_bulk_host" value={settings.email_bulk_host || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_bulk_port" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Port</label>
                    <input type="text" id="email_bulk_port" name="email_bulk_port" value={settings.email_bulk_port || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email_bulk_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                  <input type="text" id="email_bulk_username" name="email_bulk_username" value={settings.email_bulk_username || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="email_bulk_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                  <input type="password" id="email_bulk_password" name="email_bulk_password" value={settings.email_bulk_password || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_bulk_from_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Address</label>
                    <input type="email" id="email_bulk_from_address" name="email_bulk_from_address" value={settings.email_bulk_from_address || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_bulk_from_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Name</label>
                    <input type="text" id="email_bulk_from_name" name="email_bulk_from_name" value={settings.email_bulk_from_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>

            {/* Service Email */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="p-2 bg-pink-50 rounded-lg text-pink-600"><FaEnvelope /></div>
                <div>
                  <h2 className="text-xl font-semibold custom-text-neutral-800">Service Email</h2>
                  <p className="text-xs text-gray-500">Used for service notifications</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_service_host" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Host</label>
                    <input type="text" id="email_service_host" name="email_service_host" value={settings.email_service_host || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_service_port" className="block text-sm font-medium custom-text-neutral-700 mb-1">SMTP Port</label>
                    <input type="text" id="email_service_port" name="email_service_port" value={settings.email_service_port || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div>
                  <label htmlFor="email_service_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                  <input type="text" id="email_service_username" name="email_service_username" value={settings.email_service_username || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                  <label htmlFor="email_service_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                  <input type="password" id="email_service_password" name="email_service_password" value={settings.email_service_password || ''} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email_service_from_address" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Address</label>
                    <input type="email" id="email_service_from_address" name="email_service_from_address" value={settings.email_service_from_address || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div>
                    <label htmlFor="email_service_from_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">From Name</label>
                    <input type="text" id="email_service_from_name" name="email_service_from_name" value={settings.email_service_from_name || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Database Tab */}
        {activeTab === 'database' && (
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 animate-fadeIn max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><FaDatabase /></div>
              <h2 className="text-xl font-semibold custom-text-neutral-800">EDAMS Database Configuration</h2>
            </div>
            
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="edams_server_ip" className="block text-sm font-medium custom-text-neutral-700 mb-1">Server IP</label>
                  <input type="text" id="edams_server_ip" name="edams_server_ip" value={settings.edams_server_ip || ''} onChange={handleInputChange} className="form-control" placeholder="10.0.6.7" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Production */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Production Environment
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="edams_live_db_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">Database Name</label>
                      <input type="text" id="edams_live_db_name" name="edams_live_db_name" value={settings.edams_live_db_name || ''} onChange={handleInputChange} className="form-control bg-white" placeholder="silu_Edams" />
                    </div>
                    <div>
                      <label htmlFor="edams_live_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                      <input type="text" id="edams_live_username" name="edams_live_username" value={settings.edams_live_username || ''} onChange={handleInputChange} className="form-control bg-white" />
                    </div>
                    <div>
                      <label htmlFor="edams_live_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                      <input type="password" id="edams_live_password" name="edams_live_password" value={settings.edams_live_password || ''} onChange={handleInputChange} className="form-control bg-white" />
                    </div>
                  </div>
                </div>

                {/* Test */}
                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    Test Environment
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="edams_test_db_name" className="block text-sm font-medium custom-text-neutral-700 mb-1">Database Name</label>
                      <input type="text" id="edams_test_db_name" name="edams_test_db_name" value={settings.edams_test_db_name || ''} onChange={handleInputChange} className="form-control bg-white" placeholder="testsilu_Edams" />
                    </div>
                    <div>
                      <label htmlFor="edams_test_username" className="block text-sm font-medium custom-text-neutral-700 mb-1">Username</label>
                      <input type="text" id="edams_test_username" name="edams_test_username" value={settings.edams_test_username || ''} onChange={handleInputChange} className="form-control bg-white" />
                    </div>
                    <div>
                      <label htmlFor="edams_test_password" className="block text-sm font-medium custom-text-neutral-700 mb-1">Password</label>
                      <input type="password" id="edams_test_password" name="edams_test_password" value={settings.edams_test_password || ''} onChange={handleInputChange} className="form-control bg-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </form>
    </div>
  );
}

export default Settings;
